import React from "react";

interface LogoProps {
  /**
   * Accessible title for the logo for screen readers
   */
  title?: string;
  /**
   * Whether the logo is decorative (hidden from screen readers)
   */
  isDecorative?: boolean;
  /**
   * Optional ID for the logo
   */
  id?: string;
  /**
   * Optional className for additional styling
   */
  className?: string;
  /**
   * Organization logo URL - if provided, shows organization logo instead of default
   */
  organizationLogoUrl?: string;
  /**
   * Organization name for alt text and fallback
   */
  organizationName?: string;
}

/**
 * Logo component with accessibility support and organization branding
 *
 * Features:
 * - Title element for screen readers
 * - Proper ARIA attributes
 * - Can be made decorative or meaningful
 * - Support for organization-specific logos
 * - Fallback to default NeedBridge logo
 */
function getTitleId(displayTitle: string, id?: string): string | undefined {
  if (!displayTitle) return undefined;

  return id ? `${id}-title` : "logo-title";
}

function getDisplayTitle(
  title: string | undefined,
  organizationName: string | undefined,
): string {
  return title ?? organizationName ?? "Need Bridge";
}

function getOrgLogoAlt(organizationName: string | undefined): string {
  return `${organizationName ?? "Organization"} logo`;
}

function getSvgRole(isDecorative: boolean): string {
  return isDecorative ? "presentation" : "img";
}

function getSvgAriaLabelledBy(
  isDecorative: boolean,
  titleId: string | undefined,
): string | undefined {
  return isDecorative ? undefined : titleId;
}

function renderSvgTitle(
  isDecorative: boolean,
  displayTitle: string,
  titleId: string | undefined,
): React.ReactNode {
  if (isDecorative || !displayTitle) return null;

  return <title id={titleId}>{displayTitle}</title>;
}

function Logo({
  className = "",
  id,
  isDecorative = false,
  organizationLogoUrl,
  organizationName,
  title,
}: LogoProps) {
  const displayTitle = getDisplayTitle(title, organizationName);
  const titleId = getTitleId(displayTitle, id);

  if (organizationLogoUrl) {
    return (
      <img
        alt={getOrgLogoAlt(organizationName)}
        className={`h-8 w-auto ${className}`}
        height={32}
        src={organizationLogoUrl}
      />
    );
  }

  // Default NeedBridge logo
  return (
    <svg
      aria-hidden={isDecorative}
      aria-labelledby={getSvgAriaLabelledBy(isDecorative, titleId)}
      className={className}
      fill="none"
      focusable="false"
      height="184"
      id={id}
      role={getSvgRole(isDecorative)}
      style={{
        height: "2.6rem",
        width: "auto",
      }}
      viewBox="0 0 778 184"
      width="778"
      xmlns="http://www.w3.org/2000/svg"
    >
      {renderSvgTitle(isDecorative, displayTitle, titleId)}
      <path
        clipRule="evenodd"
        d="M307.073 63.2179C299.8 60.7587 293.307 57.6431 287.521 54.1017V42.1076C301.156 51.9137 319.957 59.2431 345.764 59.0732C372.513 58.8971 391.066 50.9189 403.943 40.6384V52.9915C398.699 56.462 392.779 59.5615 386.105 62.0913V142.096H399.189V152.096H288.986V142.096H307.073L307.073 63.2179ZM314.073 65.3114L314.073 142.096H331.083L331.083 68.3731C325.025 67.7392 319.363 66.6943 314.073 65.3114ZM338.083 68.9099L338.083 142.096H355.526V68.6751C352.403 68.9154 349.172 69.051 345.83 69.073C343.183 69.0904 340.602 69.0345 338.083 68.9099ZM362.526 67.9409V142.096H379.105V64.4449C373.985 65.957 368.468 67.1463 362.526 67.9409Z"
        fill="#FFC857"
        fillOpacity="0.36"
        fillRule="evenodd"
      />
      <path
        clipRule="evenodd"
        d="M406.786 151.879V183.448H396.786V0.515808H406.786V44.3892C407.09 44.3527 407.09 44.3497 407.09 44.3497L407.089 44.3426L407.087 44.3303L407.085 44.3129L407.084 44.3071C407.086 44.3158 407.091 44.3494 407.1 44.4065C407.12 44.5209 407.159 44.7286 407.227 45.0181C407.362 45.5978 407.611 46.5014 408.049 47.6385C408.923 49.9084 410.548 53.1127 413.526 56.5447C419.41 63.324 431.029 71.4999 454.012 74.5867L458.346 75.1689V149.993H458.016L458.007 152.113L406.786 151.879ZM406.786 141.879V64.0104C414.192 72.0994 426.871 80.1966 448.346 83.8403V142.069L406.786 141.879Z"
        fill="#FFC857"
        fillRule="evenodd"
      />
      <path
        clipRule="evenodd"
        d="M281.387 151.862V183.448H291.387V0.515808H281.387V44.3892C281.084 44.3527 281.084 44.3497 281.084 44.3497L281.085 44.3426L281.086 44.3303L281.088 44.3129L281.089 44.3071C281.088 44.3158 281.083 44.3494 281.073 44.4065C281.054 44.5209 281.015 44.7286 280.947 45.0181C280.812 45.5978 280.562 46.5014 280.124 47.6385C279.251 49.9084 277.626 53.1127 274.647 56.5447C268.763 63.324 257.145 71.4999 234.162 74.5867L229.827 75.1689V149.993H229.856V151.862H281.387ZM281.387 141.862V64.0104C273.982 72.0994 261.302 80.1966 239.827 83.8403V141.862H281.387Z"
        fill="#FFC857"
        fillRule="evenodd"
      />
      <path
        d="M9.62774 152.183H2.42774C0.934411 152.183 0.187744 151.436 0.187744 149.943V72.8232C0.187744 71.3298 0.934411 70.5832 2.42774 70.5832H9.62774C11.1211 70.5832 11.8677 71.3298 11.8677 72.8232V79.0632H12.5077C15.1744 73.4098 20.6144 70.5832 28.8277 70.5832H37.7877C45.0411 70.5832 50.6944 72.6632 54.7477 76.8232C58.8011 80.9832 60.8277 86.7965 60.8277 94.2632V149.943C60.8277 151.436 60.0811 152.183 58.5877 152.183H51.3877C49.8944 152.183 49.1477 151.436 49.1477 149.943V94.7432C49.1477 85.4632 44.6677 80.8232 35.7077 80.8232H26.7477C21.9477 80.8232 18.2677 82.1032 15.7077 84.6632C13.1477 87.2232 11.8677 90.8498 11.8677 95.5432V149.943C11.8677 151.436 11.1211 152.183 9.62774 152.183Z"
        fill="#FFC857"
      />
      <path
        d="M138.063 152.183H104.143C96.7827 152.183 90.9694 150.103 86.7027 145.943C82.5427 141.783 80.4627 135.97 80.4627 128.503V94.2632C80.4627 86.7965 82.5427 80.9832 86.7027 76.8232C90.9694 72.6632 96.7827 70.5832 104.143 70.5832H117.583C124.943 70.5832 130.703 72.6632 134.863 76.8232C139.023 80.9832 141.103 86.7965 141.103 94.2632V114.583C141.103 116.183 140.356 116.983 138.863 116.983H93.5827C92.5161 116.983 91.9827 117.463 91.9827 118.423V128.183C91.9827 137.463 96.4627 142.103 105.423 142.103H138.063C139.556 142.103 140.303 142.85 140.303 144.343V150.103C140.303 151.49 139.556 152.183 138.063 152.183ZM93.5827 108.503H128.143C129.209 108.503 129.743 107.97 129.743 106.903V94.5832C129.743 85.4098 125.263 80.8232 116.303 80.8232H105.423C96.4627 80.8232 91.9827 85.4098 91.9827 94.5832V106.903C91.9827 107.97 92.5161 108.503 93.5827 108.503Z"
        fill="#FFC857"
      />
      <path
        d="M216.813 152.183H182.893C175.533 152.183 169.719 150.103 165.453 145.943C161.293 141.783 159.213 135.97 159.213 128.503V94.2632C159.213 86.7965 161.293 80.9832 165.453 76.8232C169.719 72.6632 175.533 70.5832 182.893 70.5832H196.333C203.693 70.5832 209.453 72.6632 213.613 76.8232C217.773 80.9832 219.853 86.7965 219.853 94.2632V114.583C219.853 116.183 219.106 116.983 217.613 116.983H172.333C171.266 116.983 170.733 117.463 170.733 118.423V128.183C170.733 137.463 175.213 142.103 184.173 142.103H216.813C218.306 142.103 219.053 142.85 219.053 144.343V150.103C219.053 151.49 218.306 152.183 216.813 152.183ZM172.333 108.503H206.893C207.959 108.503 208.493 107.97 208.493 106.903V94.5832C208.493 85.4098 204.013 80.8232 195.053 80.8232H184.173C175.213 80.8232 170.733 85.4098 170.733 94.5832V106.903C170.733 107.97 171.266 108.503 172.333 108.503Z"
        fill="#FFC857"
      />
      <path
        d="M477.51 152.183H470.31C468.817 152.183 468.07 151.436 468.07 149.943V72.823C468.07 71.3297 468.817 70.583 470.31 70.583H477.51C479.003 70.583 479.75 71.3297 479.75 72.823V79.223H480.39C481.457 76.8764 483.483 74.8497 486.47 73.143C489.457 71.4364 492.977 70.583 497.03 70.583H503.27C504.657 70.583 505.35 71.3297 505.35 72.823V78.743C505.35 79.4897 505.19 80.0764 504.87 80.503C504.55 80.823 504.017 80.9297 503.27 80.823H494.63C489.83 80.823 486.15 82.263 483.59 85.143C481.03 88.023 479.75 91.9697 479.75 96.983V149.943C479.75 151.436 479.003 152.183 477.51 152.183Z"
        fill="#FFC857"
      />
      <path
        d="M520.589 57.143V42.103C520.589 40.6097 521.282 39.863 522.669 39.863H530.029C531.522 39.863 532.269 40.6097 532.269 42.103V57.143C532.269 58.6364 531.522 59.383 530.029 59.383H522.669C521.282 59.383 520.589 58.6364 520.589 57.143ZM520.589 149.943V72.823C520.589 71.3297 521.282 70.583 522.669 70.583H530.029C531.522 70.583 532.269 71.3297 532.269 72.823V149.943C532.269 151.436 531.522 152.183 530.029 152.183H522.829C521.335 152.183 520.589 151.436 520.589 149.943Z"
        fill="#FFC857"
      />
      <path
        d="M585.981 152.183H576.541C569.288 152.183 563.634 150.103 559.581 145.943C555.528 141.783 553.501 135.97 553.501 128.503V94.263C553.501 86.7964 555.528 80.983 559.581 76.823C563.634 72.663 569.288 70.583 576.541 70.583H585.981C594.301 70.583 599.794 73.4097 602.461 79.063H603.101V41.943C603.101 40.4497 603.794 39.703 605.181 39.703H612.541C613.928 39.703 614.621 40.4497 614.621 41.943V149.943C614.621 151.436 613.928 152.183 612.541 152.183H605.181C603.794 152.183 603.101 151.436 603.101 149.943V143.703H602.461C599.794 149.356 594.301 152.183 585.981 152.183ZM603.101 127.223V95.543C603.101 90.8497 601.821 87.223 599.261 84.663C596.701 82.103 593.021 80.823 588.221 80.823H578.461C569.608 80.823 565.181 85.463 565.181 94.743V128.023C565.181 137.303 569.608 141.943 578.461 141.943H588.221C593.021 141.943 596.701 140.663 599.261 138.103C601.821 135.543 603.101 131.916 603.101 127.223Z"
        fill="#FFC857"
      />
      <path
        d="M667.544 147.863H658.584C651.33 147.863 645.677 145.783 641.624 141.623C637.57 137.463 635.544 131.65 635.544 124.183V94.263C635.544 86.7964 637.624 80.983 641.784 76.823C646.05 72.663 651.864 70.583 659.224 70.583H693.944C695.437 70.583 696.184 71.3297 696.184 72.823V152.183C696.184 159.543 694.05 165.303 689.784 169.463C685.624 173.73 679.864 175.863 672.504 175.863H643.064C641.464 175.863 640.664 175.116 640.664 173.623V167.703C640.664 166.21 641.464 165.463 643.064 165.463H671.064C680.024 165.463 684.504 160.876 684.504 151.703V139.383H683.864C681.09 145.036 675.65 147.863 667.544 147.863ZM684.504 122.903V82.263C684.504 81.303 683.97 80.823 682.904 80.823H660.664C651.704 80.823 647.224 85.463 647.224 94.743V123.703C647.224 132.983 651.704 137.623 660.664 137.623H669.624C674.424 137.623 678.104 136.343 680.664 133.783C683.224 131.223 684.504 127.596 684.504 122.903Z"
        fill="#FFC857"
      />
      <path
        d="M774.226 152.183H740.306C732.946 152.183 727.133 150.103 722.866 145.943C718.706 141.783 716.626 135.97 716.626 128.503V94.263C716.626 86.7964 718.706 80.983 722.866 76.823C727.133 72.663 732.946 70.583 740.306 70.583H753.746C761.106 70.583 766.866 72.663 771.026 76.823C775.186 80.983 777.266 86.7964 777.266 94.263V114.583C777.266 116.183 776.519 116.983 775.026 116.983H729.746C728.679 116.983 728.146 117.463 728.146 118.423V128.183C728.146 137.463 732.626 142.103 741.586 142.103H774.226C775.719 142.103 776.466 142.85 776.466 144.343V150.103C776.466 151.49 775.719 152.183 774.226 152.183ZM729.746 108.503H764.306C765.373 108.503 765.906 107.97 765.906 106.903V94.583C765.906 85.4097 761.426 80.823 752.466 80.823H741.586C732.626 80.823 728.146 85.4097 728.146 94.583V106.903C728.146 107.97 728.679 108.503 729.746 108.503Z"
        fill="#FFC857"
      />
    </svg>
  );
}

export default Logo;
